import { FormEvent, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { Button, Icon, Text } from '@mercator-gcp/grout';
import { Label } from 'components/elements/Label';
import { Loader } from 'components/elements/Loading';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { useShareCollection } from 'hooks/useShareCollection';
import { useToast } from 'hooks/useToast';
import { EVENT_NAMES } from 'services/analytics/eventNames';
import { trackEvent } from 'services/analytics/trackEvent';
import { isValidEmail } from 'utils/emails';

const closeButtonStyle = {
  marginRight: '-12px', // compensating for empty text on the button
  marginTop: '-12px',
  marginBottom: '-12px',
};
const modalOverlayStyle = { backdropFilter: 'blur(2rem)' };
const modalContentStyle = {
  borderRadius: '8px',
  boxSizing: 'border-box',
};
const flexColumnStyle = {
  height: '100%',
  justifyContent: 'flex-start',
  alignItems: 'center',
};
const modalHeaderStyle = {
  width: '100%',
  margin: '0px',
  padding: '17px 16px 0px',
};
const modalBodyStyle = { width: '100%', padding: '24px 16px' };
const formControlStyle = { width: '100%', marginBottom: '24px' };
const labelStyle = {
  textTransform: 'none',
  fontSize: '12px',
  ml: '6px',
};
const inputStyle = {
  bgColor: 'transparent',
  marginTop: '4px',
  height: '50px',
  fontSize: '14px',
  fontWeight: '600',
  borderColor: 'brand.dark-green-200',
};
const successTextStyle = {
  textAlign: 'center',
  color: 'brand.army-green',
  fontWeight: '700',
};
const buttonContainerStyle = { display: 'flex' };
const cancelButtonStyle = { mr: '8px', p: '16px', height: 'fit-content' };
const actionButtonStyle = {
  px: '32px',
  py: '16px',
  height: 'fit-content',
};

type ShareProjectsModalProps = {
  collectionId: Number;
  onCloseClick: () => void;
};

export const ShareProjects = ({
  collectionId,
  onCloseClick,
}: ShareProjectsModalProps) => {
  const [emailInput, setEmailInput] = useState<string>('');

  const { shareCollection } = useShareCollection();
  const { renderCustomToast: toast } = useToast();

  const isEmailCurrentlyValid = useMemo(() => {
    return isValidEmail(emailInput);
  }, [emailInput]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    shareCollection.mutate(
      {
        collectionId,
        email: emailInput,
      },
      {
        onSuccess: () => {
          trackEvent(EVENT_NAMES.SHARE, {
            groupId: collectionId,
            groupType: 'pin_collection',
          });
        },
        onError: (event: any) => {
          toast({
            title: 'Error',
            description:
              event.response.data.message || 'Something went wrong. Please try again.',
            status: 'error',
            duration: 9000,
            isClosable: true,
          });
        },
      },
    );
  };

  return (
    <Modal isOpen onClose={() => onCloseClick()}>
      <ModalOverlay sx={modalOverlayStyle} />
      <ModalContent sx={modalContentStyle}>
        <FlexColumn sx={flexColumnStyle}>
          <ModalHeader sx={modalHeaderStyle}>
            <FlexRow
              alignItems="flex-start"
              justifyContent="flex-end"
              sx={closeButtonStyle}
            >
              <Button
                variant="unstyled"
                startAdornment={<Icon name={Icon.Name.CloseX} />}
                onClick={() => onCloseClick()}
              />
            </FlexRow>
            <FlexRow alignItems="center" justifyContent="space-between">
              <Text.H3>Enter email address to share projects with</Text.H3>
            </FlexRow>
          </ModalHeader>
          <ModalBody sx={modalBodyStyle}>
            {/* TODO: react-hook-form refactor once we have filters using the form library */}
            <form onSubmit={handleSubmit}>
              <FormControl sx={formControlStyle}>
                {!shareCollection.isLoading && shareCollection.status !== 'success' && (
                  <Box>
                    <Label sx={labelStyle}>Email</Label>
                    <Input
                      value={emailInput || ''}
                      type="email"
                      onChange={(e) => setEmailInput(e.target.value)}
                      onBlur={(e) => setEmailInput(e.target.value)}
                      placeholder="Enter email address"
                      variant="outlined"
                      sx={inputStyle}
                    />
                  </Box>
                )}

                {shareCollection.isLoading ? (
                  <Loader />
                ) : (
                  !shareCollection.isLoading &&
                  shareCollection.status === 'success' && (
                    <Text sx={successTextStyle}>
                      Successfully shared with {emailInput}!
                    </Text>
                  )
                )}
              </FormControl>
              <Box sx={buttonContainerStyle}>
                <Button
                  variant="functional"
                  onClick={() => onCloseClick()}
                  sx={cancelButtonStyle}
                >
                  Cancel
                </Button>
                {shareCollection.status === 'success' ? (
                  <Button
                    disabled={false}
                    variant="primary"
                    onClick={() => {
                      onCloseClick();
                      // Reset state for next submission
                    }}
                    sx={actionButtonStyle}
                  >
                    OK
                  </Button>
                ) : (
                  <Button
                    disabled={!isEmailCurrentlyValid}
                    variant="primary"
                    type="submit"
                    sx={actionButtonStyle}
                  >
                    Send Invite
                  </Button>
                )}
              </Box>
            </form>
          </ModalBody>
        </FlexColumn>
      </ModalContent>
    </Modal>
  );
};
